import { PROJECT_TYPES, normalizeProjectType } from './projectTypes';

/**
 * Shared constants and utilities for project display
 */

// Project Type icons mapping (Lucide icon names)
export const PROJECT_TYPE_ICONS = {
  renovate_sell: 'Hammer',
  renovate_rent: 'TrendingUp',
  hybrid: 'Layers',
  student_housing_rental: 'GraduationCap',
  new_development_sale: 'Building2',
  residential_rental: 'Home',
  other: 'Tag'
};

// Project Type labels - canonical list lives in lib/projectTypes.js
export const PROJECT_TYPE_LABELS = PROJECT_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

// Asset Type labels
export const ASSET_TYPE_LABELS = {
  building: 'Building',
  apartment: 'Apartment',
  apartment_building: 'Apartment Building',
  commercial_unit: 'Commercial Unit',
  student_housing: 'Student Housing',
  house: 'House',
  land: 'Land',
  mixed_use: 'Mixed Use',
  other: 'Other'
};

// Standardized Status Colors (V2 statuses)
export const STATUS_COLORS = {
  // Badge classes (for pills/tags)
  draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  pending_review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  published: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  funding: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  funded: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  in_execution: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  operating: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  exiting: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  exited: 'bg-red-500/20 text-red-400 border-red-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  // Legacy mappings for backwards compatibility
  open: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  upcoming: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  distributing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

// Solid background variants (for more prominent display)
export const STATUS_COLORS_SOLID = {
  draft: 'bg-gray-500 text-white',
  pending_review: 'bg-yellow-500 text-black',
  approved: 'bg-blue-500 text-white',
  published: 'bg-emerald-500 text-white',
  funding: 'bg-emerald-500 text-white',
  funded: 'bg-amber-500 text-black',
  in_execution: 'bg-sky-500 text-white',
  operating: 'bg-blue-500 text-white',
  exiting: 'bg-orange-500 text-white',
  exited: 'bg-red-500 text-white',
  cancelled: 'bg-red-500 text-white',
  archived: 'bg-gray-500 text-white',
  // Legacy
  open: 'bg-emerald-500 text-white',
  upcoming: 'bg-pink-500 text-white',
  distributing: 'bg-blue-500 text-white',
  executed: 'bg-purple-500 text-white'
};

// Status dot colors (for ticker/indicators)
export const STATUS_DOT_COLORS = {
  draft: 'bg-gray-500',
  pending_review: 'bg-yellow-500',
  approved: 'bg-blue-500',
  published: 'bg-emerald-500',
  funding: 'bg-emerald-500',
  funded: 'bg-amber-500',
  in_execution: 'bg-sky-400',
  operating: 'bg-blue-500',
  exiting: 'bg-orange-500',
  exited: 'bg-red-500',
  cancelled: 'bg-red-500',
  archived: 'bg-gray-500',
  // Legacy
  open: 'bg-emerald-500',
  upcoming: 'bg-pink-500',
  distributing: 'bg-blue-500',
  executed: 'bg-purple-500'
};

// Status priority for sorting (lower = higher priority)
export const STATUS_PRIORITY = {
  funding: 1,
  published: 2,
  in_execution: 3,
  operating: 4,
  funded: 5,
  exiting: 6,
  exited: 7,
  draft: 8,
  pending_review: 9,
  approved: 10,
  cancelled: 11,
  archived: 12,
  // Legacy
  open: 1,
  upcoming: 2,
  distributing: 5,
};

// Status labels (V2)
export const STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'In Review',
  approved: 'Approved',
  published: 'Published',
  funding: 'Funding',
  funded: 'Funded',
  in_execution: 'In Execution',
  operating: 'Operating',
  exiting: 'Exiting',
  exited: 'Exited',
  cancelled: 'Cancelled',
  archived: 'Archived',
  // Legacy
  upcoming: 'Upcoming',
  open: 'Open',
  distributing: 'Distributing',
  executed: 'Executed'
};

// Project Type tag colors
export const PROJECT_TYPE_COLORS = PROJECT_TYPES.reduce((acc, t) => {
  acc[t.value] = t.badge;
  return acc;
}, {});

/**
 * Get status badge class
 * @param {string} status - Project status
 * @param {boolean} solid - Use solid background
 * @returns {string} CSS classes for the badge
 */
export const getStatusBadgeClass = (status, solid = false) => {
  const colorMap = solid ? STATUS_COLORS_SOLID : STATUS_COLORS;
  return colorMap[status] || colorMap['upcoming'];
};

/**
 * Get status label
 * @param {string} status - Project status
 * @returns {string} Human-readable label
 */
export const getStatusLabel = (status) => {
  if (STATUS_LABELS[status]) {
    return STATUS_LABELS[status];
  }
  // Fallback: convert snake_case to Title Case
  if (status) {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }
  return 'Unknown';
};

/**
 * Get project type badge class
 * @param {string} projectType - Project type
 * @returns {string} CSS classes for the badge
 */
export const getProjectTypeBadgeClass = (projectType) => {
  return PROJECT_TYPE_COLORS[normalizeProjectType(projectType)] || PROJECT_TYPE_COLORS['other'];
};

/**
 * Get project type label
 * @param {string} projectType - Project type
 * @returns {string} Human-readable label
 */
export const getProjectTypeLabel = (projectType) => {
  return PROJECT_TYPE_LABELS[normalizeProjectType(projectType)] || 'Other';
};

/**
 * Get asset type label
 * @param {string} assetType - Asset type enum value
 * @returns {string} Human-readable label
 */
export const getAssetTypeLabel = (assetType) => {
  return ASSET_TYPE_LABELS[assetType] || assetType || 'Other';
};

/**
 * Sorting options for projects
 */
export const SORT_OPTIONS = [
  { value: 'publishedDate_desc', label: 'Published Date (Newest)', field: 'published_at', direction: 'desc' },
  { value: 'targetApy_desc', label: 'Target APY (Highest)', field: 'target_yield', direction: 'desc' },
  { value: 'targetRaise_desc', label: 'Target Raise (Highest)', field: 'funding_target', direction: 'desc' },
  { value: 'name_asc', label: 'Project Name (A → Z)', field: 'title', direction: 'asc' }
];

/**
 * Sort projects by the given sort key
 * @param {Array} projects - Array of projects
 * @param {string} sortKey - Sort key (e.g., 'publishedDate_desc')
 * @returns {Array} Sorted projects
 */
export const sortProjects = (projects, sortKey = 'publishedDate_desc') => {
  const sortOption = SORT_OPTIONS.find(opt => opt.value === sortKey) || SORT_OPTIONS[0];
  const { field, direction } = sortOption;

  return [...projects].sort((a, b) => {
    let valueA = a[field];
    let valueB = b[field];

    // Handle null/undefined values - sort them last
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1;
    if (valueB == null) return -1;

    // Handle date fields
    if (field === 'published_at') {
      valueA = new Date(valueA).getTime();
      valueB = new Date(valueB).getTime();
    }

    // Handle string comparison (locale-aware for names)
    if (field === 'title') {
      return direction === 'asc' 
        ? valueA.localeCompare(valueB, undefined, { sensitivity: 'base' })
        : valueB.localeCompare(valueA, undefined, { sensitivity: 'base' });
    }

    // Numeric comparison
    if (direction === 'asc') {
      return valueA - valueB;
    } else {
      return valueB - valueA;
    }
  });
};

/**
 * Format published date for display
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date (e.g., "Jan 6, 2026")
 */
export const formatPublishedDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch {
    return null;
  }
};

export default {
  PROJECT_TYPE_ICONS,
  PROJECT_TYPE_LABELS,
  ASSET_TYPE_LABELS,
  STATUS_COLORS,
  STATUS_COLORS_SOLID,
  STATUS_LABELS,
  PROJECT_TYPE_COLORS,
  SORT_OPTIONS,
  getStatusBadgeClass,
  getStatusLabel,
  getProjectTypeBadgeClass,
  getProjectTypeLabel,
  getAssetTypeLabel,
  sortProjects,
  formatPublishedDate
};
